import React from 'react';
import FeatureCards from "../../../components/FeatureCards/FeatureCards";
import CTA from "../../../components/Constant/cta/CTA";
import TimelineSection from "../../../components/TimelineSection/TimelineSection";
import WhySection from "../../../components/WhySection/WhySection";
import LocationTimeline from "../../../components/LocationTimeline/LocationTimeline";
import IntroSection from "../../../components/IntroSection/IntroSection";
import FAQList from "../../../components/Constant/FAQCard/FAQCard";
import Testimonials from "../../../components/Constant/Testimonials/Testimonials";
import DetailsSection from "../../../components/DetailsSection/DetailsSection";
import InfoBlocks from "../../../components/InfoBlocks/InfoBlocks";
import ServiceCards from "../../../components/ServiceCards/ServiceCards";

export default function UnitedKingdomJurisdiction() {

    return (
        <main className='main'>
            <IntroSection
                title="United Kingdom (UK) Jurisdiction"
                subtitle="Company Formation & Financial Licensing in the UK"
                description="The United Kingdom (UK) is one of the world’s most trusted business jurisdictions, offering global credibility, access to double taxation treaties, and a stable legal system. UK entities are widely used for trading, holding structures, fintech startups, and regulated activities."
                badges={[
                    "Prestigious Jurisdiction",
                    "Fast Incorporation",
                    "Double Tax Treaties"
                ]}
                buttonText="Request a UK Jurisdiction Proposal →"
                buttonLink="/quote"
                imageSrc={require('../../../assets/images/jurisdictions/uk-jurisdiction.png')}
                imageAlt="United Kingdom Company Formation & Licensing"
            />

            <section className='section'>
                <div className="container">

                    <WhySection
                        title='Why Choose the UK?'
                        blocks={[
                            {
                                items: [
                                    {
                                        title: "Prestige & Recognition",
                                        text: "UK companies are globally accepted."
                                    },
                                    {
                                        title: "Fast Incorporation",
                                        text: "Same-day registration possible."
                                    },
                                    {
                                        title: "Tax Planning",
                                        text: "Corporate tax at 25% but exemptions for non-resident structures."
                                    },
                                    {
                                        title: "Wide Treaty Network",
                                        text: "Double taxation agreements with 130+ countries."
                                    },
                                    {
                                        title: "Regulatory Framework",
                                        text: "Respected FCA regulation for finance and fintech."
                                    },
                                    {
                                        title: "Access to Banking",
                                        text: "UK companies can open accounts in Europe, UK, and offshore centers."
                                    }
                                ]
                            }
                        ]}
                    />

                    <InfoBlocks
                        title="Company Formation in the UK"
                        blocks={[
                            {
                                title: "Private Limited Company (Ltd)",
                                items: [
                                    { text: "Most common structure for entrepreneurs." },
                                    { text: "Advantages: Low cost, limited liability, international recognition." },
                                    { text: "Use Cases: Trading, consulting, IT, fintech startups." }
                                ]
                            },
                            {
                                title: "Limited Liability Partnership (LLP)",
                                items: [
                                    { text: "Flexible structure, often used for international tax planning." },
                                    { text: "Partners taxed individually; profits can be tax-free if non-resident." },
                                    { text: "Use Cases: Holding, joint ventures, consulting." }
                                ]
                            },
                            {
                                title: "Public Limited Company (PLC)",
                                items: [
                                    { text: "Suitable for larger companies and raising capital." },
                                    { text: "Must have at least £50,000 share capital." }
                                ]
                            },
                            {
                                title: "Branch or Representative Office",
                                items: [
                                    { text: "For foreign companies expanding into the UK." }
                                ]
                            }
                        ]}
                    />

                    <InfoBlocks
                        title="Licenses & Regulated Activities in the UK"
                        blocks={[
                            {
                                title: "FCA Authorization",
                                items: [{ text: "Required for forex brokers, payment institutions, asset managers." }]
                            },
                            {
                                title: "EMI & PSP Licenses",
                                items: [{ text: "For fintech/payment startups." }]
                            },
                            {
                                title: "Crypto Registration",
                                items: [{ text: "FCA supervises UK crypto exchanges and wallet providers." }]
                            },
                            {
                                title: "Insurance & Reinsurance",
                                items: [{ text: "Strong London-based insurance sector." }]
                            },
                            {
                                title: "Banking License",
                                items: [{ text: "Regulated by the Bank of England (significant capital required)." }]
                            }
                        ]}
                    />

                    <LocationTimeline
                        title="Incorporation Process"
                        item={[
                            {
                                title: "Step 1",
                                text: "Choose entity type (Ltd, LLP, PLC)."
                            },
                            {
                                title: "Step 2",
                                text: "Submit incorporation documents to Companies House."
                            },
                            {
                                title: "Step 3",
                                text: "Receive Certificate of Incorporation (same day possible)."
                            },
                            {
                                title: "Step 4",
                                text: "Open bank account."
                            },
                            {
                                title: "Step 5",
                                text: "Apply for licenses if regulated activities are required."
                            },
                            {
                                title: "Timeline",
                                text: "1–3 business days for Ltd; longer for regulated entities."
                            }
                        ]}
                    />

                    <ServiceCards
                        title='Advantages of a UK Company'
                        services={[
                            {
                                title: "🌍 Global Recognition",
                                text: "Respected internationally."
                            },
                            {
                                title: "⚖️ Double Tax Treaties",
                                text: "Protect against double taxation."
                            },
                            {
                                title: "💼 Flexible Structures",
                                text: "Ltd and LLP suited to offshore use."
                            },
                            {
                                title: "🏦 Banking Access",
                                text: "High acceptance rate."
                            },
                            {
                                title: "🚀 Regulatory Opportunities",
                                text: "Fintech, EMI, brokerage, asset management."
                            }
                        ]}
                    />

                    <FAQList data={faqData} title='Frequently Asked Questions (FAQ)'/>

                    <CTA
                        title="Ready to incorporate in the UK?"
                        description="Offshore Advisory provides fast company registration, FCA licensing guidance, and banking support."
                        buttonText="Book a Free Consultation for UK Company Formation →"
                        buttonLink="/quote"
                    />

                </div>
            </section>
        </main>
    );
};

const faqData = [
    {
        question: "Are UK companies tax-free?",
        answer: "No, corporate tax is 25%. However, LLPs with non-resident partners and no UK activity may not pay UK tax."
    },
    {
        question: "How fast can I set up a company?",
        answer: "Same-day incorporation is possible for Ltds."
    },
    {
        question: "Do UK companies need accounting?",
        answer: "Yes, annual filings are mandatory."
    },
    {
        question: "Is the UK good for fintech?",
        answer: "Yes, the UK is a Tier-1 fintech hub with strong FCA regulation."
    },
    {
        question: "Can I register a UK company remotely?",
        answer: "Yes, full remote incorporation is available with Offshore Advisory."
    }
];
